import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  MdDashboard,
  MdPictureAsPdf,
  MdEmail,
  MdMarkEmailRead,
  MdCheckCircle,
  MdWarningAmber,
  MdFileDownload,
  MdExpandMore,
} from "react-icons/md";
import { pdf } from "@react-pdf/renderer";
import { toast } from "react-toastify";
import Card from "../../../components/card";
import api from "../../../services/axios";
import ClientReportPdf, { ClientReportData } from "./ClientReportPdf";
import TimesheetDetailPdf from "./TimesheetDetailPdf";
import SearchableSelect from "../../../components/searchableSelect/SearchableSelect";
import { usePermissions } from "../../../auth/PermissionProvider";
import { Permissions } from "../../../auth/permissions";
import {
  getAreas,
  getBillings,
  getHours,
  getOverview,
  getProjects,
  getTasks,
  getTimesheetDetails,
} from "../../../services/clientPortalApi";
import {
  BILLING_STATUS_LABELS,
  ClientPortalAreas,
  ClientPortalBilling,
  ClientPortalHoursMonth,
  ClientPortalOverview,
  ClientPortalProject,
  ClientPortalTask,
} from "../../../interfaces/ClientPortal";
import { ProjectTask, TaskType } from "../../../interfaces/ProjectTask";
import TaskGantt from "../projectTasks/components/TaskGantt";
import GanttPdfButton from "../projectTasks/components/GanttPdfButton";
import MultiSelect from "../../../components/searchableSelect/MultiSelect";
import TicketsTab from "./components/TicketsTab";
import FilaEquipeTab from "./components/FilaEquipeTab";
import FilasTab from "./components/FilasTab";
import EquipeTab from "./components/EquipeTab";
import OrganogramaTab from "./components/OrganogramaTab";
import PortalTabBar from "./components/PortalTabBar";
import { mensagemDeAreaNegada, PortalTab as Tab, temAlgumaArea } from "./portalAreas";
import { mensagemDoServidor } from "../../../utils/mensagemDoServidor";

const currency = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

const PROJECT_STATUS: Record<number, string> = { 1: "Ativo", 2: "Concluído", 3: "Cancelado" };

const MONTHS_SHORT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const MONTHS_FULL = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const ClientPortalPage: React.FC = () => {
  /*
    Interno pode pré-visualizar qualquer cliente; o Client fica travado no próprio. O que separa
    os dois é poder ver a lista de clientes — daí `clients.view` ser exatamente a pergunta certa,
    e não uma lista de papéis que precisava crescer a cada papel novo.
  */
  const { can } = usePermissions();
  const isInternal = can(Permissions.ClientsView);

  const [clientsList, setClientsList] = useState<{ id: number; name: string }[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | undefined>(undefined);
  const [tab, setTab] = useState<Tab>("overview");
  const [year, setYear] = useState<number>(new Date().getFullYear());
  /*
    `months` é a seleção real: lista vazia = ano inteiro, um item = um mês, vários = período solto.
    As outras abas (Visão geral, Faturas) continuam raciocinando com um número só, então `month`
    é derivado: só vira mês específico quando há exatamente um selecionado. Assim a seleção múltipla
    entra sem mexer no filtro que essas abas já usavam.
  */
  const [months, setMonths] = useState<number[]>([]);
  const month = months.length === 1 ? months[0] : 0;

  const [overview, setOverview] = useState<ClientPortalOverview | null>(null);
  const [projects, setProjects] = useState<ClientPortalProject[]>([]);
  const [hours, setHours] = useState<ClientPortalHoursMonth[]>([]);
  const [tasks, setTasks] = useState<ClientPortalTask[]>([]);
  const [billings, setBillings] = useState<ClientPortalBilling[]>([]);
  const [loading, setLoading] = useState(false);
  const [notLinked, setNotLinked] = useState(false);
  // null = ainda não carregou. Sem isso as abas piscariam todas antes de sumirem.
  const [areas, setAreas] = useState<ClientPortalAreas | null>(null);
  const [erroDeArea, setErroDeArea] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [mailBusy, setMailBusy] = useState<null | "client" | "self">(null);

  // Gera o "resumão" do cliente em PDF (horas + cronograma + entregas).
  const generateReportPdf = async () => {
    if (selectedClientId === undefined) return;
    setPdfBusy(true);
    try {
      const { data } = await api.get(`/Financial/ClientReport/${selectedClientId}`);
      const blob = await pdf(<ClientReportPdf data={data as ClientReportData} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report-${(data?.clientName || "cliente").toString().replace(/\s+/g, "-").toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Erro ao gerar PDF do report", e);
      toast.error("Erro ao gerar o PDF do report.");
    } finally {
      setPdfBusy(false);
    }
  };

  // Envia o report por e-mail: para o cliente, ou para o próprio admin logado (toSelf).
  const sendReportEmail = async (mode: "client" | "self") => {
    if (selectedClientId === undefined) return;
    setMailBusy(mode);
    try {
      const { data } = await api.post(
        `/Financial/SendClientReport/${selectedClientId}?toSelf=${mode === "self"}`
      );
      toast.success(`Report enviado para ${data?.recipient ?? "destinatário"}.`);
    } catch (e: any) {
      console.error("Erro ao enviar report", e);
      toast.error(mensagemDoServidor(e, "Erro ao enviar o report por e-mail."));
    } finally {
      setMailBusy(null);
    }
  };

  // Carrega lista de clientes apenas para usuários internos (seletor de preview).
  useEffect(() => {
    if (!isInternal) return;
    api
      .get("/Client/Clients")
      .then((r) =>
        setClientsList(
          (Array.isArray(r.data) ? r.data : [])
            .filter((c: any) => c.flActive)
            .map((c: any) => ({ id: c.id, name: c.legalName ?? c.tradeName ?? `Cliente #${c.id}` }))
        )
      )
      .catch((e) => console.error("Erro ao carregar clientes", e));
  }, [isInternal]);

  const canQuery = !isInternal || selectedClientId !== undefined;

  /*
    Organização da equipe: no modo interno é o cliente escolhido no seletor; no modo cliente, a da
    sessão (o overview devolve o `clientId` do vínculo). Sem ela, as abas da equipe não renderizam.
  */
  const equipeOrgId = isInternal ? selectedClientId : overview?.clientId;
  const podeGerirEquipe = can(Permissions.ClienteEquipeGerir);

  const reload = () => {
    if (!canQuery) return;
    setLoading(true);
    setNotLinked(false);
    setErroDeArea(null);
    // Qualquer 403 de área mostra a mensagem do servidor; o resto segue vazio como antes.
    const falhou = <T,>(vazio: T) => (e: any): T => {
      const negada = mensagemDeAreaNegada(e);
      if (negada) setErroDeArea(negada);
      return vazio;
    };
    getAreas()
      .then((a) => {
        setAreas(a);
        // Só chama o que a pessoa pode ver: evita 403 previsível e o aviso desnecessário.
        return Promise.all([
          getOverview(selectedClientId).catch((e) => {
            if (e?.response?.status === 404) setNotLinked(true);
            return falhou<ClientPortalOverview | null>(null)(e);
          }),
          a.podeVerProjetos ? getProjects(selectedClientId).catch(falhou([])) : [],
          a.podeVerHoras ? getHours(selectedClientId, 36).catch(falhou([])) : [],
          a.podeVerProjetos ? getTasks(selectedClientId).catch(falhou([])) : [],
          a.podeVerFinanceiro ? getBillings(selectedClientId).catch(falhou([])) : [],
        ]);
      })
      .then(([ov, pr, hr, tk, bl]) => {
        setOverview(ov);
        setProjects(pr);
        setHours(hr);
        setTasks(tk);
        setBillings(bl);
      })
      .catch((e) => {
        setAreas(null);
        setErroDeArea(
          mensagemDeAreaNegada(e) ?? "Não foi possível carregar o portal. Recarregue a página."
        );
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, [selectedClientId, isInternal]);

  // Anos disponíveis a partir das horas e faturas (mais recente primeiro).
  const availableYears = useMemo(() => {
    const set = new Set<number>([new Date().getFullYear()]);
    hours.forEach((h) => set.add(h.year));
    billings.forEach((b) => set.add(b.yearRef));
    return Array.from(set).sort((a, b) => b - a);
  }, [hours, billings]);

  const showFilter = tab === "overview" || tab === "hours" || tab === "billings";

  return (
    <div className="mt-5 flex animate-fade-up flex-col gap-4">
      {/* Cabeçalho */}
      <Card extra="overflow-hidden p-0">
        <div className="dv-aura relative overflow-hidden bg-dv-gradient px-5 py-5 text-white shadow-dv-glow">
          <div
            className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-white/10 blur-3xl"
            aria-hidden="true"
          />
          <div className="relative flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/25 backdrop-blur"
                aria-hidden="true"
              >
                <MdDashboard className="h-6 w-6" />
              </span>
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 font-display text-xs font-bold uppercase tracking-[0.18em] text-white ring-1 ring-white/20">
                  Portal do Cliente
                </span>
                <h1 className="mt-1.5 text-2xl font-extrabold tracking-tight text-white">
                  {overview?.clientName ? `Olá, ${overview.clientName}` : "Acompanhamento"}
                </h1>
                <p className="text-xs capitalize text-white/80">
                {new Date().toLocaleDateString("pt-BR", {
                  weekday: "long",
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })}
                </p>
              </div>
            </div>
            {isInternal && (
              <div className="flex flex-col gap-2 md:items-end">
                <div className="w-64">
                  <label htmlFor="previewClient" className="mb-1 block text-sm font-semibold text-white/90">
                    Pré-visualizar cliente
                  </label>
                  <SearchableSelect
                    placeholder="Selecione um cliente…"
                    value={selectedClientId ?? null}
                    onChange={(v) => setSelectedClientId(v == null ? undefined : Number(v))}
                    options={clientsList.map((c) => ({ label: c.name, value: c.id }))}
                  />
                </div>
                {selectedClientId !== undefined && (
                  <div className="flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      onClick={generateReportPdf}
                      disabled={pdfBusy}
                      title="Baixar o resumão do cliente em PDF (horas, cronograma e entregas)"
                      className="flex items-center justify-center gap-1.5 rounded-xl bg-white/15 px-4 py-2 text-xs font-semibold text-white ring-1 ring-white/25 backdrop-blur transition-all hover:bg-white/25 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <MdPictureAsPdf className="h-4 w-4" aria-hidden="true" /> {pdfBusy ? "Gerando..." : "Gerar PDF"}
                    </button>
                    <button
                      type="button"
                      onClick={() => sendReportEmail("client")}
                      disabled={mailBusy !== null}
                      title="Enviar o report por e-mail para o cliente"
                      className="flex items-center justify-center gap-1.5 rounded-xl bg-white/15 px-4 py-2 text-xs font-semibold text-white ring-1 ring-white/25 backdrop-blur transition-all hover:bg-white/25 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <MdEmail className="h-4 w-4" aria-hidden="true" /> {mailBusy === "client" ? "Enviando..." : "Enviar ao cliente"}
                    </button>
                    <button
                      type="button"
                      onClick={() => sendReportEmail("self")}
                      disabled={mailBusy !== null}
                      title="Receber o report no seu e-mail (admin)"
                      className="flex items-center justify-center gap-1.5 rounded-xl bg-white px-4 py-2 text-xs font-bold text-ink-900 shadow-dv-md transition-all hover:bg-gray-100 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <MdMarkEmailRead className="h-4 w-4" aria-hidden="true" /> {mailBusy === "self" ? "Enviando..." : "Enviar p/ mim"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Resumo fixo (storytelling) — sempre visível acima das abas, em qualquer aba. */}
        {canQuery && !notLinked && areas && temAlgumaArea(areas) && overview && <PortalSummary overview={overview} tasks={tasks} />}

        {canQuery && !notLinked && (
          <div className="flex flex-col gap-3 border-t border-gray-100 px-5 py-3 dark:border-white/10 lg:flex-row lg:items-center lg:justify-between">
            {areas && temAlgumaArea(areas) ? (
              <PortalTabBar
                areas={areas}
                active={tab}
                onChange={setTab}
                podeAtender={!isInternal && can(Permissions.ItsmFilaAtender)}
                podeCoordenar={!isInternal && can(Permissions.ItsmFilaCoordenar)}
                podeGerirEquipe={podeGerirEquipe}
                podeLerEquipe={!isInternal && can(Permissions.PortalProjects) && !!areas?.podeVerProjetos}
                podeLerOrganograma={!isInternal && can(Permissions.ClientPortalView)}
              />
            ) : (
              <span />
            )}

            {showFilter && (
              <div className="flex items-center gap-2">
                <PeriodPicker months={months} onChange={setMonths} />
                <select
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  className={filterCls}
                  aria-label="Ano"
                >
                  {availableYears.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </Card>

      {isInternal && !canQuery && (
        <Card extra="p-10">
          <p className="text-center text-sm text-gray-700 dark:text-gray-300">
            Selecione um cliente acima para pré-visualizar o portal dele.
          </p>
        </Card>
      )}

      {notLinked && (
        <Card extra="p-10">
          <p className="text-center text-sm text-gray-700 dark:text-gray-300">
            Seu login ainda não está vinculado a um cliente. Contate o administrador.
          </p>
        </Card>
      )}

      {canQuery && !notLinked && erroDeArea && (
        <Card extra="p-5">
          <p role="alert" className="text-center text-sm font-medium text-red-700 dark:text-red-300">
            {erroDeArea}
          </p>
        </Card>
      )}

      {canQuery && !notLinked && !loading && areas && !temAlgumaArea(areas) && (
        <Card extra="p-10">
          <p className="text-center text-sm text-gray-700 dark:text-gray-300">
            Seu acesso ao portal ainda não inclui nenhuma área. Fale com quem administra sua conta.
          </p>
        </Card>
      )}

      {canQuery && !notLinked && (loading || (areas && temAlgumaArea(areas))) && (
        <Card extra="p-5">
          {loading ? (
            <div className="py-12 text-center text-sm text-gray-700 dark:text-gray-300">Carregando…</div>
          ) : tab === "overview" ? (
            <Overview overview={overview} projects={projects} tasks={tasks} hours={hours} billings={billings} year={year} month={month} />
          ) : tab === "schedule" ? (
            <Schedule tasks={tasks} />
          ) : tab === "projects" ? (
            <Projects projects={projects} />
          ) : tab === "hours" ? (
            <Hours hours={hours} year={year} months={months} clientId={selectedClientId} clientName={overview?.clientName} />
          ) : tab === "billings" ? (
            <Billings billings={billings} year={year} month={month} />
          ) : tab === "fila" ? (
            <FilaEquipeTab />
          ) : tab === "filas" ? (
            // Quem gere usa as rotas do cliente (interno: sempre); o coordenador comum, as da sessão.
            <FilasTab orgId={podeGerirEquipe ? equipeOrgId : undefined} clienteId={equipeOrgId} />
          ) : tab === "equipe" ? (
            equipeOrgId !== undefined && <EquipeTab key={equipeOrgId} orgId={equipeOrgId} podeGerir={podeGerirEquipe} />
          ) : tab === "organograma" ? (
            equipeOrgId !== undefined && <OrganogramaTab key={equipeOrgId} orgId={equipeOrgId} podeGerir={podeGerirEquipe} />
          ) : (
            <TicketsTab readOnly={isInternal} />
          )}
        </Card>
      )}
    </div>
  );
};

const filterCls =
  "rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

/** Rótulo curto do período: vazio = ano inteiro, 1 = nome do mês, 2+ = "N meses". */
export const periodLabel = (months: number[]): string => {
  if (months.length === 0) return "Ano inteiro";
  if (months.length === 1) return MONTHS_FULL[months[0] - 1];
  if (months.length === 12) return "Ano inteiro";
  return `${months.length} meses`;
};

/*
  Seletor de período. Um `select` nativo com `multiple` não cabe nesta barra e é ruim de usar no
  toque, então é um popover com caixas de seleção. "Ano inteiro" é a lista VAZIA e não os 12 meses
  marcados: assim o rótulo e a consulta continuam corretos quando o ano ainda não tem 12 meses.

  ⚠ O PAINEL VAI EM PORTAL, e isso não é preferência. Esta barra de filtros mora dentro do `Card`
  com `overflow-hidden` (e do bloco do gradiente, que também recorta). Painel posicionado como
  filho é CORTADO por esses ancestrais, e `z-index` não resolve: empilhamento não atravessa
  `overflow: hidden`. Portal para o `body` mais posição `fixed` calculada do gatilho é o que torna
  o painel imune a qualquer ancestral que recorte ou crie contexto de empilhamento.

  Regra geral para esta base: dropdown, popover ou tooltip dentro de card sai em portal.
*/
const PeriodPicker: React.FC<{ months: number[]; onChange: (m: number[]) => void }> = ({ months, onChange }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number; flip: boolean }>({ top: 0, right: 0, flip: false });
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  const PANEL_W = 224; // w-56
  const PANEL_H_EST = 210;

  // Recalcula a posição a partir do retângulo do gatilho. `fixed` usa a viewport, então serve
  // igual para a página rolada. Vira para cima quando não há espaço abaixo.
  const place = React.useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const flip = r.bottom + PANEL_H_EST > window.innerHeight && r.top > PANEL_H_EST;
    setPos({
      top: flip ? r.top - 4 : r.bottom + 4,
      right: Math.max(8, window.innerWidth - r.right),
      flip,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      // O painel não é mais descendente do gatilho, então os dois precisam ser testados.
      if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    // `true` na captura pega rolagem de qualquer container, não só a da janela.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, place]);

  const toggle = (m: number) => {
    const next = months.includes(m) ? months.filter((x) => x !== m) : [...months, m].sort((a, b) => a - b);
    onChange(next.length === 12 ? [] : next);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${filterCls} flex min-w-[9.5rem] items-center justify-between gap-2`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Período"
      >
        <span className="truncate">{periodLabel(months)}</span>
        <MdExpandMore className="h-4 w-4 shrink-0 opacity-60" aria-hidden="true" />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={{
            position: "fixed",
            top: pos.top,
            right: pos.right,
            width: PANEL_W,
            transform: pos.flip ? "translateY(-100%)" : undefined,
            zIndex: 1000,
          }}
          className="rounded-xl border border-gray-200 bg-white p-2 shadow-2xl dark:border-white/10 dark:bg-navy-900"
        >
          <button
            type="button"
            onClick={() => onChange([])}
            className={`mb-1 w-full rounded-lg px-2.5 py-2 text-left text-sm font-semibold transition-colors ${
              months.length === 0
                ? "bg-dv-blue-500/10 text-dv-blue-600 dark:text-dv-cyan-300"
                : "text-gray-800 hover:bg-gray-50 dark:text-white dark:hover:bg-navy-700"
            }`}
          >
            Ano inteiro
          </button>
          <div className="grid grid-cols-3 gap-1">
            {MONTHS_SHORT.map((m, i) => {
              const n = i + 1;
              const on = months.includes(n);
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => toggle(n)}
                  aria-pressed={on}
                  className={`rounded-lg px-1 py-1.5 text-xs font-semibold transition-colors ${
                    on
                      ? "bg-dv-blue-500 text-white"
                      : "text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-navy-700"
                  }`}
                >
                  {m}
                </button>
              );
            })}
          </div>
          {months.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="mt-2 w-full rounded-lg px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-navy-700"
            >
              Limpar seleção
            </button>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

// ── Resumo fixo (storytelling, sempre no topo) ──────────────────────────────────

/** Só conta o que veio: campo omitido = área não liberada, e não "zero atrasadas". */
const pontosDeAtencao = (overview: ClientPortalOverview, utilization: number | null): string[] => {
  const out: string[] = [];
  const atrasadas = overview.overdueTasks ?? 0;
  const pendentes = overview.billingPendingCount ?? 0;
  if (atrasadas > 0) out.push(`${atrasadas} tarefa${atrasadas > 1 ? "s" : ""} atrasada${atrasadas > 1 ? "s" : ""}`);
  if (pendentes > 0) out.push(`${pendentes} fatura${pendentes > 1 ? "s" : ""} pendente${pendentes > 1 ? "s" : ""}`);
  if (utilization !== null && utilization >= 100) out.push("horas contratadas esgotadas");
  return out;
};

/**
 * Áreas sobre as quais o resumo PODE afirmar saúde: as que têm indicador no overview (campo
 * presente). Ausência não é zero (D4) — quem só vê Chamados não pode ler "Tudo em dia" sobre
 * projetos e faturas que não enxerga. Vazio = não mostrar selo nem banner.
 */
const areasComIndicador = (overview: ClientPortalOverview): string[] => {
  const out: string[] = [];
  if (overview.overdueTasks !== undefined) out.push("Projetos");
  if (overview.totalContractedHours !== undefined) out.push("Horas");
  if (overview.billingPendingCount !== undefined) out.push("Financeiro");
  return out;
};

/** "Projetos", "Projetos e Horas", "Projetos, Horas e Financeiro". */
const listaDeAreas = (areas: string[]): string =>
  areas.length <= 1 ? areas.join("") : `${areas.slice(0, -1).join(", ")} e ${areas[areas.length - 1]}`;

const PortalSummary: React.FC<{ overview: ClientPortalOverview; tasks: ClientPortalTask[] }> = ({ overview, tasks }) => {
  const utilization =
    (overview.totalContractedHours ?? 0) > 0
      ? Math.round(((overview.totalWorkedHours ?? 0) / overview.totalContractedHours!) * 100)
      : null;

  const attention = pontosDeAtencao(overview, utilization);
  const healthy = attention.length === 0;
  const escopo = areasComIndicador(overview);

  const nextDue = useMemo(
    () =>
      tasks
        .filter((t) => t.dueDate && t.statusCategory !== "Done" && t.statusCategory !== "Cancelled")
        .sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime())[0],
    [tasks]
  );

  return (
    <div className="border-t border-gray-100 px-5 py-3.5 dark:border-white/10">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        {escopo.length > 0 && <span
          className={`inline-flex max-w-full items-center gap-1.5 self-start rounded-full px-3 py-1.5 text-xs font-semibold ${
            healthy
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
              : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
          }`}
        >
          {healthy ? <MdCheckCircle className="h-4 w-4 shrink-0" /> : <MdWarningAmber className="h-4 w-4 shrink-0" />}
          <span className="truncate">{healthy ? `${listaDeAreas(escopo)} em dia` : attention.join(" · ")}</span>
        </span>}

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:gap-3">
          {overview.activeProjects !== undefined && (
            <MiniStat label="Próxima entrega" value={nextDue ? fmtDate(nextDue.dueDate) : "—"} />
          )}
          {overview.totalWorkedHours !== undefined && (
            <MiniStat label="Horas trabalhadas" value={`${overview.totalWorkedHours}h`} />
          )}
          {overview.billingOpenAmount !== undefined && (
            <MiniStat label="Em aberto" value={currency(overview.billingOpenAmount)} tone={overview.billingOpenAmount > 0 ? "red" : undefined} />
          )}
          {overview.activeProjects !== undefined && (
            <MiniStat label="Projetos ativos" value={`${overview.activeProjects}/${overview.totalProjects ?? 0}`} />
          )}
        </div>
      </div>
    </div>
  );
};

const MiniStat: React.FC<{ label: string; value: React.ReactNode; tone?: "red" }> = ({ label, value, tone }) => (
  <div className="rounded-xl bg-gray-50 px-3 py-2 dark:bg-navy-900">
    <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">{label}</p>
    <p className={`font-display text-sm font-bold tabular-nums ${tone === "red" ? "text-red-600 dark:text-red-400" : "text-navy-700 dark:text-white"}`}>
      {value}
    </p>
  </div>
);

// ── Overview ───────────────────────────────────────────────────────────────────

const Overview: React.FC<{
  overview: ClientPortalOverview | null;
  projects: ClientPortalProject[];
  tasks: ClientPortalTask[];
  hours: ClientPortalHoursMonth[];
  billings: ClientPortalBilling[];
  year: number;
  month: number;
}> = ({ overview, projects, tasks, hours, billings, year, month }) => {
  const periodLabel = month > 0 ? `${MONTHS_FULL[month - 1]}/${year}` : `${year}`;

  const period = useMemo(() => {
    const h = hours
      .filter((x) => x.year === year && (month === 0 || x.month === month))
      .reduce((s, x) => s + x.hours, 0);
    const fb = billings.filter((b) => b.yearRef === year && (month === 0 || b.monthRef === month));
    return {
      hours: h,
      billed: fb.reduce((s, b) => s + b.amount, 0),
      received: fb.filter((b) => b.status === 3).reduce((s, b) => s + b.amount, 0),
      open: fb.filter((b) => b.status !== 3 && b.status !== 4).reduce((s, b) => s + b.amount, 0),
      pendingCount: fb.filter((b) => b.status === 1).length,
    };
  }, [hours, billings, year, month]);

  // Próximas entregas: tarefas em aberto com prazo, da mais próxima para a mais distante.
  const upcoming = useMemo(
    () =>
      tasks
        .filter((t) => t.dueDate && t.statusCategory !== "Done" && t.statusCategory !== "Cancelled")
        .sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime())
        .slice(0, 5),
    [tasks]
  );

  // Ritmo de consumo: média mensal das horas recentes → projeção de esgotamento do contrato.
  const pace = useMemo(() => {
    if (!overview || (overview.totalContractedHours ?? 0) <= 0) return null;
    const recent = [...hours]
      .filter((h) => h.hours > 0)
      .sort((a, b) => b.year - a.year || b.month - a.month)
      .slice(0, 6);
    if (recent.length === 0) return null;
    const avg = recent.reduce((s, h) => s + h.hours, 0) / recent.length;
    if (avg <= 0) return null;
    const remaining = Math.max(0, overview.totalContractedHours! - (overview.totalWorkedHours ?? 0));
    const monthsLeft = remaining / avg;
    const end = new Date();
    end.setMonth(end.getMonth() + Math.round(monthsLeft));
    return {
      avg: Math.round(avg),
      remaining: Math.round(remaining),
      depleted: remaining <= 0,
      endLabel: end.toLocaleDateString("pt-BR", { month: "long", year: "numeric" }),
    };
  }, [overview, hours]);

  const activeProjects = useMemo(() => projects.filter((p) => p.status === 1), [projects]);

  if (!overview) return <p className="py-6 text-center text-sm text-gray-700 dark:text-gray-300">Ainda não há dados para exibir neste período.</p>;

  const utilization =
    (overview.totalContractedHours ?? 0) > 0
      ? Math.round(((overview.totalWorkedHours ?? 0) / overview.totalContractedHours!) * 100)
      : null;

  // Banner de status: junta os pontos de atenção atuais numa única leitura.
  const attention = pontosDeAtencao(overview, utilization);
  const healthy = attention.length === 0;
  const escopo = areasComIndicador(overview);

  // O backend omite os campos de área não liberada: campo presente = cartão visível.
  const temProjetos = overview.totalProjects !== undefined;
  const temHoras = overview.totalContractedHours !== undefined;
  const temFinanceiro = overview.billingOpenAmount !== undefined;

  return (
    <div className="flex flex-col gap-6">
      {/* Status — leitura instantânea de "está tudo bem?" (só sobre o que a pessoa vê) */}
      {escopo.length > 0 && <div
        className={`flex items-start gap-3 rounded-2xl border p-4 ${
          healthy
            ? "border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-900/20"
            : "border-amber-200 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-900/20"
        }`}
      >
        <span
          className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
            healthy
              ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300"
              : "bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300"
          }`}
          aria-hidden="true"
        >
          {healthy ? <MdCheckCircle className="h-5 w-5" /> : <MdWarningAmber className="h-5 w-5" />}
        </span>
        <div className="min-w-0">
          <p className={`text-sm font-bold ${healthy ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"}`}>
            {healthy ? `${listaDeAreas(escopo)} em dia` : "Pontos de atenção"}
          </p>
          <p className="text-xs text-gray-700 dark:text-gray-300">
            {healthy
              ? "Nada atrasado ou pendente no momento."
              : attention.join(" · ")}
          </p>
        </div>
      </div>}

      {/* Próximas entregas + Ritmo de horas */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Próximas entregas — área Projetos */}
        {temProjetos && <div className="lg:col-span-2">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
            Próximas entregas
          </p>
          {upcoming.length === 0 ? (
            <p className="rounded-xl border border-dashed border-gray-200 py-8 text-center text-sm text-gray-700 dark:border-white/10 dark:text-gray-300">
              Nenhuma entrega agendada.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {upcoming.map((t) => {
                const chip = dueChip(t);
                return (
                  <div
                    key={t.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 dark:border-white/5 dark:bg-navy-900"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-navy-700 dark:text-white" title={t.title}>{t.title}</p>
                      <p className="truncate text-xs text-gray-700 dark:text-gray-300">{t.projectName ?? "—"}</p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className="text-xs font-medium tabular-nums text-gray-700 dark:text-gray-300">{fmtDate(t.dueDate)}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${chip.cls}`}>{chip.text}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>}

        {/* Ritmo de horas — área Horas */}
        {temHoras && <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
            Horas do contrato
          </p>
          <div className="flex h-[calc(100%-1.75rem)] flex-col justify-center rounded-xl border border-gray-100 bg-gray-50 p-4 dark:border-white/5 dark:bg-navy-900">
            {utilization === null ? (
              <p className="text-center text-sm text-gray-700 dark:text-gray-300">Sem horas contratadas.</p>
            ) : (
              <>
                <div className="mb-1 flex items-baseline justify-between">
                  <span className="font-display text-2xl font-bold tabular-nums text-navy-700 dark:text-white">{overview.totalWorkedHours}h</span>
                  <span className="text-xs text-gray-700 dark:text-gray-300">de {overview.totalContractedHours}h</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-navy-800">
                  <div
                    className={`h-full ${utilization >= 100 ? "bg-red-500" : utilization >= 80 ? "bg-amber-500" : "bg-green-500"}`}
                    style={{ width: `${Math.min(100, utilization)}%` }}
                  />
                </div>
                <p className="mt-1 text-right text-[11px] font-semibold text-gray-700 dark:text-gray-300">{utilization}% consumido</p>
                {pace && (
                  <p className="mt-3 border-t border-gray-200 pt-3 text-xs text-gray-700 dark:border-white/10 dark:text-gray-300">
                    {pace.depleted ? (
                      <>Horas contratadas <span className="font-semibold text-red-600 dark:text-red-400">esgotadas</span>.</>
                    ) : (
                      <>
                        No ritmo de <span className="font-semibold">{pace.avg}h/mês</span>, as{" "}
                        <span className="font-semibold">{pace.remaining}h</span> restantes terminam por volta de{" "}
                        <span className="font-semibold capitalize text-navy-700 dark:text-white">{pace.endLabel}</span>.
                      </>
                    )}
                  </p>
                )}
              </>
            )}
          </div>
        </div>}
      </div>

      {/* Progresso dos projetos ativos */}
      {temProjetos && activeProjects.length > 0 && (
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
            Andamento dos projetos ativos
          </p>
          <div className="flex flex-col gap-3">
            {activeProjects.map((p) => {
              const pct = p.totalTasks > 0 ? Math.round((p.doneTasks / p.totalTasks) * 100) : 0;
              return (
                <div key={p.projectId} className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 dark:border-white/5 dark:bg-navy-900">
                  <div className="mb-1.5 flex items-center justify-between gap-3">
                    <p className="min-w-0 truncate text-sm font-semibold text-navy-700 dark:text-white" title={p.projectName}>{p.projectName}</p>
                    {p.overdueTasks > 0 ? (
                      <span className="shrink-0 rounded-full bg-red-100 px-2.5 py-0.5 text-[10px] font-bold text-red-700 dark:bg-red-900/40 dark:text-red-300">
                        {p.overdueTasks} atrasada{p.overdueTasks > 1 ? "s" : ""}
                      </span>
                    ) : (
                      <span className="shrink-0 rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-bold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                        No prazo
                      </span>
                    )}
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-navy-800">
                    <div className="h-full bg-gradient-to-r from-dv-blue-500 to-dv-cyan-400" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="mt-1 flex justify-between text-[11px] text-gray-700 dark:text-gray-300">
                    <span>{p.doneTasks}/{p.totalTasks} tarefas concluídas</span>
                    <span className="tabular-nums">{pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Resumo financeiro do período — área Financeiro */}
      {temFinanceiro && <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
          Financeiro · {periodLabel}
        </p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {temHoras && <Kpi label="Horas no período" value={`${period.hours}h`} />}
          <Kpi label="Faturado" value={currency(period.billed)} />
          <Kpi label="Recebido" value={currency(period.received)} tone="green" />
          <Kpi label="Em aberto" value={currency(period.open)} hint={`${period.pendingCount} pendente(s)`} tone={period.open > 0 ? "red" : undefined} />
        </div>
      </div>}
    </div>
  );
};

// Chip de prazo para a lista de próximas entregas (atrasada / hoje / em X dias).
const dueChip = (t: ClientPortalTask): { text: string; cls: string } => {
  const red = "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300";
  const amber = "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
  const blue = "bg-dv-blue-100 text-dv-blue-700 dark:bg-dv-blue-900/40 dark:text-dv-cyan-300";
  if (t.isOverdue) return { text: "Atrasada", cls: red };
  const days = Math.ceil((new Date(t.dueDate!).getTime() - Date.now()) / 86_400_000);
  if (days <= 0) return { text: "Hoje", cls: amber };
  if (days === 1) return { text: "Amanhã", cls: amber };
  if (days <= 7) return { text: `Em ${days} dias`, cls: amber };
  return { text: `Em ${days} dias`, cls: blue };
};

// ── Projects ───────────────────────────────────────────────────────────────────

const Projects: React.FC<{ projects: ClientPortalProject[] }> = ({ projects }) => {
  if (projects.length === 0)
    return <p className="py-6 text-center text-sm text-gray-700 dark:text-gray-300">Nenhum projeto.</p>;
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {projects.map((p) => {
        const util = p.contractedHours ? Math.round((p.workedHours / p.contractedHours) * 100) : null;
        return (
          <div key={p.projectId} className="rounded-xl border border-gray-100 p-4 dark:border-white/10">
            <div className="mb-2 flex items-start justify-between gap-2">
              <p className="min-w-0 truncate font-semibold text-navy-700 dark:text-white" title={p.projectName}>{p.projectName}</p>
              <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-700 dark:bg-navy-900 dark:text-gray-300">
                {PROJECT_STATUS[p.status] ?? p.statusName}
              </span>
            </div>
            <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700 dark:text-gray-300">
              <span>Início: {fmtDate(p.startDate)}</span>
              <span>Previsão: {fmtDate(p.estimatedEndDate)}</span>
            </div>
            <div className="mb-2 text-xs text-gray-700 dark:text-gray-300">
              {p.workedHours}h trabalhadas{p.contractedHours ? ` de ${p.contractedHours}h` : ""}
            </div>
            {util !== null && (
              <div className="mb-2 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-navy-900">
                <div
                  className={`h-full ${util >= 100 ? "bg-red-500" : util >= 80 ? "bg-amber-500" : "bg-green-500"}`}
                  style={{ width: `${Math.min(100, util)}%` }}
                />
              </div>
            )}
            <div className="flex flex-wrap gap-2 text-[11px] font-semibold">
              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-gray-700 dark:bg-navy-900 dark:text-gray-300">
                {p.totalTasks} tarefas
              </span>
              <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">{p.doneTasks} concluídas</span>
              {p.overdueTasks > 0 && (
                <span className="rounded-full bg-red-100 px-2.5 py-1 text-red-700 dark:bg-red-900/40 dark:text-red-300">{p.overdueTasks} atrasadas</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Hours ──────────────────────────────────────────────────────────────────────

const Hours: React.FC<{
  hours: ClientPortalHoursMonth[];
  year: number;
  months: number[];
  clientId?: number;
  clientName?: string;
}> = ({ hours, year, months, clientId, clientName }) => {
  const [exporting, setExporting] = useState(false);

  // Horas por mês do ano selecionado (Jan..Dez), 0 quando não há registro.
  const byMonth = useMemo(() => {
    const map = new Map<number, number>();
    hours.filter((h) => h.year === year).forEach((h) => map.set(h.month, h.hours));
    return Array.from({ length: 12 }, (_, i) => ({ month: i + 1, hours: map.get(i + 1) ?? 0 }));
  }, [hours, year]);

  // Lista efetiva de meses: seleção vazia significa o ano inteiro.
  const selected = useMemo(
    () => (months.length === 0 ? Array.from({ length: 12 }, (_, i) => i + 1) : months),
    [months]
  );

  const maxHours = Math.max(1, ...byMonth.map((m) => m.hours));
  /*
    Soma de decimais acumula erro de ponto flutuante — 230.67000000000002 apareceu na tela.
    Arredonda no fim, uma vez, em vez de formatar em cada lugar que exibe.
  */
  const periodTotal = useMemo(
    () => Math.round(byMonth.filter((m) => selected.includes(m.month)).reduce((s, m) => s + m.hours, 0) * 100) / 100,
    [byMonth, selected]
  );
  const hasAny = byMonth.some((m) => m.hours > 0);
  // Só faz sentido exportar o que tem lançamento; sem isso o PDF sai com páginas vazias.
  const canExport = selected.some((n) => (byMonth[n - 1]?.hours ?? 0) > 0);

  /*
    Exporta os lançamentos do período selecionado. A API é por mês, então busca em paralelo e
    concatena — o PDF agrupa por mês e traz o gráfico do ano no topo.
  */
  const exportPdf = async () => {
    if (!canExport) {
      toast.info("Não há lançamentos no período selecionado.");
      return;
    }
    setExporting(true);
    try {
      const comHoras = selected.filter((n) => (byMonth[n - 1]?.hours ?? 0) > 0);
      const lotes = await Promise.all(comHoras.map((n) => getTimesheetDetails(clientId, n, year)));
      const entries = lotes.flat();

      const blob = await pdf(
        <TimesheetDetailPdf
          data={{
            clientName: clientName || "Cliente",
            year,
            months: comHoras,
            byMonth,
            entries,
          }}
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const sufixo =
        comHoras.length === 1 ? `${year}-${String(comHoras[0]).padStart(2, "0")}` : `${year}`;
      a.href = url;
      a.download = `lancamentos-${sufixo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Erro ao exportar lançamentos", e);
      toast.error("Erro ao exportar os lançamentos.");
    } finally {
      setExporting(false);
    }
  };

  const botaoLabel =
    months.length === 0 ? "Exportar PDF do ano" : months.length === 1 ? "Exportar PDF do mês" : "Exportar PDF do período";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-navy-700 dark:text-white">
            Horas trabalhadas · {periodLabel(months)}
            {months.length === 12 || months.length === 0 ? ` · ${year}` : `/${year}`}
          </p>
          <p className="text-xs text-gray-700 dark:text-gray-300">
            Selecione o período no topo: o ano inteiro, um mês ou vários. Exporte os lançamentos para validar cada apontamento do time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={exportPdf}
            disabled={exporting || !canExport}
            title={canExport ? "Baixar os lançamentos do período em PDF" : "Não há lançamentos no período selecionado"}
            className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-gray-200 dark:hover:bg-navy-700"
          >
            <MdFileDownload className="h-4 w-4" aria-hidden="true" />
            {exporting ? "Gerando…" : botaoLabel}
          </button>
          <div className="rounded-xl bg-gray-50 px-4 py-2 text-right dark:bg-navy-900">
            <p className="text-[11px] text-gray-700 dark:text-gray-300">Total no período</p>
            <p className="font-display text-xl font-bold tabular-nums text-dv-blue-600 dark:text-dv-cyan-300">{periodTotal}h</p>
          </div>
        </div>
      </div>

      {!hasAny ? (
        <p className="py-8 text-center text-sm text-gray-700 dark:text-gray-300">Sem horas registradas em {year}.</p>
      ) : (
        <div className="flex items-end gap-2 overflow-x-auto pb-2" style={{ height: 200 }}>
          {byMonth.map((m) => {
            const on = selected.includes(m.month);
            return (
              <div key={m.month} className="flex flex-1 flex-col items-center justify-end" style={{ minWidth: 32 }}>
                <span className={`mb-1 text-[10px] font-semibold tabular-nums ${on ? "text-navy-700 dark:text-white" : "text-gray-300 dark:text-gray-600"}`}>
                  {m.hours || ""}
                </span>
                <div
                  className={`w-full max-w-[28px] rounded-t-md transition-all ${on ? "bg-gradient-to-t from-dv-blue-500 to-dv-cyan-400" : "bg-gray-200 dark:bg-navy-700"}`}
                  style={{ height: `${(m.hours / maxHours) * 150}px` }}
                  title={`${MONTHS_FULL[m.month - 1]}: ${m.hours}h`}
                />
                <span className="mt-1 text-[10px] text-gray-700 dark:text-gray-300">{MONTHS_SHORT[m.month - 1]}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Cronograma (Gantt + lista) ───────────────────────────────────────────────────

const Schedule: React.FC<{ tasks: ClientPortalTask[] }> = ({ tasks }) => {
  const [epicFilter, setEpicFilter] = useState<(string | number)[]>([]);

  const epics = useMemo(() => tasks.filter((t) => t.type === TaskType.Epic), [tasks]);

  // Filtro por épico: mantém o(s) épico(s) selecionado(s) + toda a sua subárvore.
  const viewTasks = useMemo(() => {
    if (epicFilter.length === 0) return tasks;
    const selected = new Set(epicFilter.map(Number));
    const byId = new Map(tasks.map((t) => [t.id, t]));
    const epicOf = (t: ClientPortalTask): number | null => {
      let cur: ClientPortalTask | undefined = t;
      let guard = 0;
      while (cur && guard++ < 12) {
        if (cur.type === TaskType.Epic) return cur.id;
        if (cur.parentTaskId == null) return null;
        cur = byId.get(cur.parentTaskId);
      }
      return null;
    };
    return tasks.filter((t) => {
      const e = epicOf(t);
      return e != null && selected.has(e);
    });
  }, [tasks, epicFilter]);

  if (tasks.length === 0)
    return <p className="py-8 text-center text-sm text-gray-700 dark:text-gray-300">Nenhuma tarefa cadastrada.</p>;

  // Só para o empty-state: o TaskGantt recebe a lista COMPLETA (como na tela admin) — ele
  // mesmo esconde folhas sem data e renderiza épicos/pais como envelope derivado dos filhos.
  // Pré-filtrar por data aqui removia os épicos do Gantt (não têm datas próprias).
  const hasDated = viewTasks.some((t) => t.startDate || t.dueDate);
  const overdueCount = viewTasks.filter((t) => t.isOverdue && t.statusCategory !== "Done").length;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm font-semibold text-navy-700 dark:text-white">Cronograma (Gantt)</p>
            {overdueCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-[11px] font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">
                <MdWarningAmber className="h-3.5 w-3.5" aria-hidden="true" />
                {overdueCount} atrasada{overdueCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {epics.length > 0 && (
              <div className="w-64" title="Filtrar por épico (vazio = todos)">
                <MultiSelect
                  placeholder="Todos os épicos"
                  value={epicFilter}
                  onChange={setEpicFilter}
                  options={epics.map((e) => ({ label: e.title, value: e.id }))}
                />
              </div>
            )}
            {hasDated && (
              <GanttPdfButton tasks={viewTasks as ProjectTask[]} modoCliente showProject title="Cronograma" fileName="cronograma.pdf" />
            )}
          </div>
        </div>
        {!hasDated ? (
          <p className="rounded-xl border border-dashed border-gray-200 py-8 text-center text-sm text-gray-700 dark:border-white/10 dark:text-gray-300">
            As tarefas ainda não têm datas no cronograma.
          </p>
        ) : (
          // Somente leitura para o cliente (clique no card não edita).
          <TaskGantt tasks={viewTasks as ProjectTask[]} onEdit={() => {}} showProject modoCliente />
        )}
      </div>

      <div>
        <p className="mb-2 text-sm font-semibold text-navy-700 dark:text-white">Tarefas</p>
        <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-navy-900">
                {["Tarefa", "Projeto", "Status", "Prazo"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {viewTasks.map((t) => (
                <tr key={t.id} className="border-b border-gray-100 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700">
                  <td className="px-4 py-3 font-medium text-navy-700 dark:text-white">{t.title}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{t.projectName ?? "—"}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-700 dark:bg-navy-900 dark:text-gray-300">
                      {t.statusName}
                    </span>
                  </td>
                  <td className={`px-4 py-3 tabular-nums ${t.isOverdue ? "font-semibold text-red-600 dark:text-red-400" : "text-gray-700 dark:text-gray-300"}`}>
                    {fmtDate(t.dueDate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Billings ───────────────────────────────────────────────────────────────────

const billingTone: Record<number, string> = {
  1: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  2: "bg-dv-blue-100 text-dv-blue-700 dark:bg-dv-blue-900/40 dark:text-dv-cyan-300",
  3: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  4: "bg-gray-100 text-gray-700 dark:bg-navy-900 dark:text-gray-300",
  5: "bg-dv-magenta-100 text-dv-magenta-700 dark:bg-dv-magenta-900/40 dark:text-dv-magenta-300",
};

const Billings: React.FC<{ billings: ClientPortalBilling[]; year: number; month: number }> = ({
  billings,
  year,
  month,
}) => {
  const filtered = useMemo(
    () => billings.filter((b) => b.yearRef === year && (month === 0 || b.monthRef === month)),
    [billings, year, month]
  );
  const periodTotal = filtered.reduce((s, b) => s + b.amount, 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm font-semibold text-navy-700 dark:text-white">
          Faturas {month > 0 ? `· ${MONTHS_FULL[month - 1]}/${year}` : `· ${year}`}
        </p>
        <div className="rounded-xl bg-gray-50 px-4 py-2 text-right dark:bg-navy-900">
          <p className="text-[11px] text-gray-700 dark:text-gray-300">Total no período</p>
          <p className="font-display text-lg font-bold tabular-nums text-navy-700 dark:text-white">{currency(periodTotal)}</p>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-700 dark:text-gray-300">Nenhuma fatura no período selecionado.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-navy-900">
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Competência</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Projeto</th>
                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Valor</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Status</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">NF</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} className="border-b border-gray-100 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700">
                  <td className="px-4 py-3 tabular-nums text-gray-700 dark:text-gray-300">{String(b.monthRef).padStart(2, "0")}/{b.yearRef}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{b.projectName ?? "—"}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-navy-700 dark:text-white">{currency(b.amount)}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${billingTone[b.status] ?? "bg-gray-100 text-gray-700 dark:bg-navy-900 dark:text-gray-300"}`}>
                      {BILLING_STATUS_LABELS[b.status] ?? b.statusName}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{b.invoiceNumber ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Small UI helpers ───────────────────────────────────────────────────────────

const Kpi: React.FC<{ label: string; value: React.ReactNode; hint?: string; tone?: "red" | "green" }> = ({
  label,
  value,
  hint,
  tone,
}) => (
  <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-white/5 dark:bg-navy-900">
    <p className="text-xs text-gray-700 dark:text-gray-300">{label}</p>
    <p
      className={`font-display text-xl font-bold tabular-nums tracking-tight ${
        tone === "red"
          ? "text-red-600 dark:text-red-400"
          : tone === "green"
          ? "text-emerald-600 dark:text-emerald-400"
          : "text-navy-700 dark:text-white"
      }`}
    >
      {value}
    </p>
    {hint && <p className="text-[11px] text-gray-700 dark:text-gray-300">{hint}</p>}
  </div>
);

export default ClientPortalPage;
